import { useTranslation } from "react-i18next";
import { useState } from "react";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { Card, CardContent } from "@/components/ui/card";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import { toast } from "@/utils/customToast.tsx";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
// import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface PaymentsStepProps {
  onNext: () => void;
}

const PaymentsStep: React.FC<PaymentsStepProps> = ({ onNext }) => {
  const { t } = useTranslation(["onboarding", "finances"]);
  const { setShouldReload } = useUserStore();
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const handleCardClick = (type: string) => {
    if (type !== "stripe") {
      // Only allow selection of credits
      setSelectedPaymentPlan(type);
    }
  };

  const handleConfirmPlan = async () => {
    if (selectedPaymentPlan !== "credit") return;

    setIsLoading(true);
    try {
      await axiosApiInstance.post(API_ENDPOINTS.TUTOR.FINANCES.CREDITS_ONBOARDING, {});

      // Set localStorage to show invite help in next step
      localStorage.setItem("should_show_invite_help", "true");

      setShouldReload(true);
      onNext();
    } catch (error: any) {
      console.error("Credits onboarding failed:", error);

      if (error.response?.status === 403) {
        if (error.response.data?.error === "payment_plan_already_selected") {
          toast.error(t("finances:confirmation.alreadySelected", "Payment plan already selected"));
        } else {
          toast.error(
            t(
              "finances:confirmation.noPermission",
              "You don't have permission to perform this action",
            ),
          );
        }
      } else if (error.response?.status === 401) {
        toast.error(t("finances:confirmation.unauthorized", "You need to be logged in"));
      } else {
        toast.error(
          t("finances:confirmation.error", "Failed to activate credits system. Please try again."),
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex w-full cursor-default flex-col items-center select-none">
      {/* Background circles */}
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute -top-50 -left-50 size-120 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 opacity-30 transition-colors"
        initial={{ opacity: 0 }}
        layoutId="circle1"
        transition={{
          default: { duration: 0.75 },
          layout: { duration: 0.75 },
        }}
      />
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute -right-50 -bottom-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-blue-500/20 opacity-30 transition-colors"
        initial={{ opacity: 0.2 }}
        layoutId="circle2"
        transition={{
          layout: { duration: 0.75 },
        }}
      />

      {/* Main Content */}
      <div className="flex h-full w-full flex-col p-2">
        {/* Header */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-accent-foreground text-2xl font-bold lg:text-3xl">
            {t("finances:paymentsSetup.planTitle", "Choose Your Payment Plan")}
          </h2>
        </motion.div>

        {/* Payment Plan Selection Cards */}
        <motion.div
          layout
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.3, layout: { duration: 0.5 } }}
        >
          <motion.div
            layout
            className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2"
            transition={{ layout: { duration: 0.5 } }}
          >
            {/* Stripe Plan Card */}
            <motion.div
              layout
              animate={{ opacity: 0.6, scale: 1 }}
              className="group relative cursor-not-allowed"
              initial={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.23, 1, 0.32, 1] }}
            >
              <Card className="bg-muted/20 relative h-full shadow-lg backdrop-blur-xl transition-all duration-500 group-hover:shadow-lg">
                {/* Decorative Circles */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    className="absolute -top-6 -right-6 h-12 w-12 rounded-full bg-blue-500/10 opacity-40 transition-opacity group-hover:opacity-60"
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    className="absolute -bottom-4 -left-4 h-8 w-8 rounded-full bg-purple-500/10 opacity-40 transition-opacity group-hover:opacity-60"
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  />
                </div>

                {/* Soon Badge */}
                <motion.div
                  animate={{ y: [0, -2, 0] }}
                  className="bg-accent-foreground text-background absolute -top-2 right-4 z-10 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold shadow-lg"
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Icon height="0.75rem" icon="mdi:clock-outline" width="0.75rem" />
                  {t("common:soon", "Soon")}
                </motion.div>

                <CardContent className="relative z-10 flex h-full flex-col p-6">
                  <motion.div
                    layout
                    className="mb-4 flex items-start gap-4"
                    transition={{ layout: { duration: 0.4 } }}
                  >
                    {/* Radio Button */}
                    <motion.div
                      layout
                      className="bg-muted/30 border-muted-foreground mt-1 flex h-5 min-h-5 w-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full border-2"
                      transition={{ layout: { duration: 0.4 } }}
                    />

                    {/* Icon */}
                    <motion.div
                      layout
                      className="bg-muted/30 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg"
                      transition={{ layout: { duration: 0.4 } }}
                    >
                      <Icon
                        className="text-muted-foreground"
                        height="1.5rem"
                        icon="logos:stripe"
                        width="1.5rem"
                      />
                    </motion.div>

                    {/* Content */}
                    <motion.div
                      layout
                      className="min-w-0 flex-1"
                      transition={{ layout: { duration: 0.4 } }}
                    >
                      <h3 className="text-muted-foreground mb-1 text-lg font-bold">
                        {t("finances:paymentsSetup.stripe.title", "Stripe")}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {t("finances:paymentsSetup.stripe.subtitle", "Dynamic Billing and Payouts")}
                      </p>
                    </motion.div>
                  </motion.div>

                  {/* Availability - below content */}
                  <motion.div
                    layout
                    className="border-muted/20 mt-auto border-t pt-3"
                    transition={{ layout: { duration: 0.4 } }}
                  >
                    <p className="text-muted-foreground text-center text-xs">
                      {t(
                        "finances:paymentsSetup.stripe.availability",
                        "unavailable in some countries",
                      )}
                    </p>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Credits Plan Card */}
            <motion.div
              layout
              animate={{ opacity: 1, scale: 1 }}
              className="group relative cursor-pointer"
              initial={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.6, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
              onClick={() => handleCardClick("credit")}
            >
              <Card
                className={`relative h-full overflow-hidden shadow-lg backdrop-blur-xl transition-all duration-500 hover:shadow-lg ${
                  selectedPaymentPlan === "credit"
                    ? "bg-foreground/95 shadow-xl"
                    : "bg-foreground/80 hover:bg-foreground/85"
                }`}
              >
                {/* Decorative Circles */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <motion.div
                    animate={{
                      scale: selectedPaymentPlan === "credit" ? [1, 1.2, 1] : [1, 1.1, 1],
                    }}
                    className={`absolute -top-6 -right-6 h-12 w-12 rounded-full opacity-40 transition-opacity group-hover:opacity-70 ${
                      selectedPaymentPlan === "credit" ? "bg-green-500/15" : "bg-emerald-500/10"
                    }`}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    animate={{
                      scale: selectedPaymentPlan === "credit" ? [1, 1.3, 1] : [1, 1.2, 1],
                    }}
                    className={`absolute -bottom-4 -left-4 h-8 w-8 rounded-full opacity-40 transition-opacity group-hover:opacity-70 ${
                      selectedPaymentPlan === "credit" ? "bg-blue-500/15" : "bg-teal-500/10"
                    }`}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1.5,
                    }}
                  />
                </div>

                <CardContent className="relative z-10 flex h-full flex-col p-6">
                  <motion.div
                    layout
                    className="mb-4 flex items-start gap-4"
                    transition={{ layout: { duration: 0.4 } }}
                  >
                    {/* Radio Button */}
                    <motion.div
                      layout
                      animate={{
                        scale: selectedPaymentPlan === "credit" ? 1.1 : 1,
                        boxShadow:
                          selectedPaymentPlan === "credit"
                            ? "0 0 0 3px rgba(var(--accent-foreground), 0.1)"
                            : "0 0 0 0px rgba(var(--accent-foreground), 0)",
                      }}
                      className={`mt-1 flex h-5 min-h-5 w-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                        selectedPaymentPlan === "credit"
                          ? "bg-accent-foreground border-accent-foreground"
                          : "bg-muted/50 border-muted-foreground"
                      }`}
                      transition={{
                        duration: 0.4,
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      <motion.div
                        layout
                        animate={{
                          scale: selectedPaymentPlan === "credit" ? 1 : 0,
                          opacity: selectedPaymentPlan === "credit" ? 1 : 0,
                        }}
                        className="bg-background h-2.5 w-2.5 rounded-full"
                        transition={{
                          duration: 0.3,
                          type: "spring",
                          stiffness: 400,
                          damping: 25,
                        }}
                      />
                    </motion.div>

                    {/* Icon */}
                    <motion.div
                      layout
                      animate={{
                        scale: selectedPaymentPlan === "credit" ? 1.05 : 1,
                        backgroundColor:
                          selectedPaymentPlan === "credit"
                            ? "rgba(var(--accent-foreground), 0.25)"
                            : "rgba(var(--accent-foreground), 0.2)",
                      }}
                      className="bg-accent-foreground/20 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg"
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <Icon
                        className="text-accent-foreground"
                        height="1.5rem"
                        icon="ph:coins-thin"
                        width="1.5rem"
                      />
                    </motion.div>

                    {/* Content */}
                    <motion.div
                      layout
                      className="min-w-0 flex-1"
                      transition={{ layout: { duration: 0.4 } }}
                    >
                      <h3 className="text-accent-foreground mb-1 text-lg font-bold">
                        {t("finances:paymentsSetup.credit.title", "Credits")}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {t("finances:paymentsSetup.credit.subtitle", "Pre-paid lesson system")}
                      </p>
                    </motion.div>
                  </motion.div>

                  {/* Availability - below content */}
                  <motion.div
                    layout
                    className="border-muted/20 mt-auto border-t pt-3"
                    transition={{ layout: { duration: 0.4 } }}
                  >
                    <p className="text-muted-foreground text-center text-xs">
                      {t("finances:paymentsSetup.credit.availability", "Available worldwide")}
                    </p>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Detailed Plan Overview */}
        <motion.div
          layout
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          transition={{
            duration: 0.6,
            delay: 0.6,
            ease: [0.23, 1, 0.32, 1],
            layout: { duration: 0.5 },
          }}
        >
          <motion.div
            layout
            className="mx-auto w-full max-w-5xl space-y-6"
            transition={{ layout: { duration: 0.5 } }}
          >
            <motion.div
              key={selectedPaymentPlan || "default"}
              layout
              animate={{ opacity: 1, x: 0 }}
              className="flex-1"
              exit={{ opacity: 0, x: 20 }}
              initial={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            >
              {selectedPaymentPlan === "credit" ? (
                <div className="space-y-6">
                  {/* Header */}
                  {/* Mobile layout: icon + title in a row, description below */}
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="flex flex-col gap-2 sm:hidden"
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-accent-foreground/20 flex items-center justify-center rounded-xl p-3">
                        <Icon
                          className="text-accent-foreground"
                          height="2rem"
                          icon="ph:coins-thin"
                          width="2rem"
                        />
                      </div>
                      <h3 className="text-accent-foreground text-lg font-bold">
                        {t(
                          "finances:creditsGuide.title",
                          "How Our Direct Payment & Credits System Works",
                        )}
                      </h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {t(
                        "finances:creditsGuide.subtitle",
                        "A flexible and transparent way to get booked and paid.",
                      )}
                    </p>
                  </motion.div>

                  {/* Desktop/tablet layout: original design */}
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="hidden items-start gap-4 sm:flex"
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-accent-foreground/20 flex h-16 w-16 items-center justify-center rounded-xl">
                      <Icon
                        className="text-accent-foreground"
                        height="2rem"
                        icon="ph:coins-thin"
                        width="2rem"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-accent-foreground mb-1 text-xl font-bold sm:text-2xl">
                        {t(
                          "finances:creditsGuide.title",
                          "How Our Direct Payment & Credits System Works",
                        )}
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        {t(
                          "finances:creditsGuide.subtitle",
                          "A flexible and transparent way to get booked and paid.",
                        )}
                      </p>
                    </div>
                  </motion.div>

                  {/* The Big Picture */}
                  <div>
                    <div className="text-muted-foreground mb-3 text-xs tracking-wider uppercase">
                      {t("finances:creditsGuide.bigPicture.title", "The Big Picture")}
                    </div>
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-foreground/60 hover:bg-foreground/70 relative overflow-hidden rounded-2xl p-5 transition-all"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        animate={{ opacity: [0.35, 0.5, 0.35], scale: [1.05, 1.15, 1.05] }}
                        className="absolute top-1/2 left-1/2 size-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/25 opacity-50 blur-3xl"
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <motion.div
                        animate={{ opacity: [0.3, 0.45, 0.3], scale: [1, 1.1, 1] }}
                        className="absolute top-6 right-6 size-44 rounded-full bg-emerald-500/25 opacity-50 blur-3xl"
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="relative z-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[
                          {
                            icon: "mdi:coin-outline",
                            title: t(
                              "finances:creditsGuide.steps.step1Title",
                              "Tutor pre-pays platform fee",
                            ),
                            desc: t(
                              "finances:creditsGuide.steps.step1Desc",
                              "Purchase credits up front. Each credit covers our small one-time fee for 1 lesson hour.",
                            ),
                          },
                          {
                            icon: "mdi:calendar-check-outline",
                            title: t(
                              "finances:creditsGuide.steps.step2Title",
                              "Student books a lesson",
                            ),
                            desc: t(
                              "finances:creditsGuide.steps.step2Desc",
                              "When a lesson is booked, 1 of your credits is used to secure the slot.",
                            ),
                          },
                          {
                            icon: "mdi:bank-transfer",
                            title: t(
                              "finances:creditsGuide.steps.step3Title",
                              "Student pays you directly",
                            ),
                            desc: t(
                              "finances:creditsGuide.steps.step3Desc",
                              "They pay you in full via your chosen method. We don’t touch this money.",
                            ),
                          },
                        ].map((item) => (
                          <div key={item.title as string} className="rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-accent-foreground"
                                height="1rem"
                                icon={item.icon}
                                width="1rem"
                              />
                              <span className="text-accent-foreground text-sm font-semibold">
                                {item.title}
                              </span>
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                              {item.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  <Separator className="bg-border/30" />

                  {/* Tutors */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="mdi:teacher"
                        width="1rem"
                      />
                      <div className="text-accent-foreground text-sm font-semibold">
                        {t(
                          "finances:creditsGuide.tutor.title",
                          "For tutors: full control over your earnings",
                        )}
                      </div>
                    </div>
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-foreground/60 hover:bg-foreground/70 relative mt-3 overflow-hidden rounded-2xl p-5 transition-all"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        animate={{ opacity: [0.4, 0.6, 0.4], scale: [1.05, 1.15, 1.05] }}
                        className="absolute top-1/2 -right-14 size-56 -translate-y-1/2 rounded-full bg-emerald-500/30 opacity-60 blur-3xl"
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <motion.div
                        animate={{ opacity: [0.35, 0.5, 0.35], scale: [1, 1.1, 1] }}
                        className="absolute bottom-6 left-6 size-44 rounded-full bg-blue-500/25 opacity-50 blur-3xl"
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="relative z-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {[
                          {
                            icon: "ph:coins-thin",
                            title: t("finances:creditsGuide.tutor.buyCreditsTitle", "Buy credits"),
                            desc: t(
                              "finances:creditsGuide.tutor.buyCreditsDesc",
                              "Keep credits in your account so students can book.",
                            ),
                          },
                          {
                            icon: "mdi:wallet",
                            title: t(
                              "finances:creditsGuide.tutor.setPaymentMethodTitle",
                              "Set your payment method",
                            ),
                            desc: t(
                              "finances:creditsGuide.tutor.setPaymentMethodDesc",
                              "Configure Bank Transfer, PayPal, Revolut, etc. Shared after booking.",
                            ),
                          },
                          {
                            icon: "mdi:cash-multiple",
                            title: t(
                              "finances:creditsGuide.tutor.receivePaymentTitle",
                              "Receive your payment",
                            ),
                            desc: t(
                              "finances:creditsGuide.tutor.receivePaymentDesc",
                              "Student sends the full lesson fee directly to you.",
                            ),
                          },
                          {
                            icon: "mdi:check-circle",
                            title: t(
                              "finances:creditsGuide.tutor.confirmTitle",
                              "Confirm and teach",
                            ),
                            desc: t(
                              "finances:creditsGuide.tutor.confirmDesc",
                              "Mark the lesson as Paid once funds arrive and proceed.",
                            ),
                          },
                        ].map((row) => (
                          <div key={row.title as string} className="rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-accent-foreground"
                                height="1rem"
                                icon={row.icon}
                                width="1rem"
                              />
                              <span className="text-accent-foreground text-sm font-semibold">
                                {row.title}
                              </span>
                            </div>
                            {row.title ===
                              t("finances:creditsGuide.tutor.buyCreditsTitle", "Buy credits") && (
                              <div className="text-accent-foreground/90 bg-accent-foreground/10 mt-2 inline-block rounded-md px-2 py-1 text-[11px] font-semibold">
                                {t(
                                  "finances:creditsGuide.creditEquation",
                                  "We charge 1 credit per lesson hour",
                                )}
                              </div>
                            )}
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                              {row.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="text-accent-foreground/90 mt-3 text-xs opacity-90">
                        {t(
                          "finances:creditsGuide.tutor.benefit",
                          "Benefit: you receive 100% of your lesson fee without any deductions from us.",
                        )}
                      </div>
                    </motion.div>
                  </div>

                  <Separator className="bg-border/30" />

                  {/* Students */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="mdi:school-outline"
                        width="1rem"
                      />
                      <div className="text-accent-foreground text-sm font-semibold">
                        {t(
                          "finances:creditsGuide.student.title",
                          "For students: a simple and direct way to learn",
                        )}
                      </div>
                    </div>
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-foreground/60 hover:bg-foreground/70 relative mt-3 overflow-hidden rounded-2xl p-5 transition-all"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        animate={{ opacity: [0.35, 0.5, 0.35], scale: [1.05, 1.15, 1.05] }}
                        className="absolute top-1/2 left-6 size-52 -translate-y-1/2 rounded-full bg-blue-500/30 opacity-60 blur-3xl"
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <motion.div
                        animate={{ opacity: [0.3, 0.45, 0.3], scale: [1, 1.1, 1] }}
                        className="absolute top-8 right-10 size-40 rounded-full bg-emerald-500/25 opacity-50 blur-3xl"
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="relative z-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                          {
                            icon: "mdi:bookmark-outline",
                            title: t(
                              "finances:creditsGuide.student.findTutorTitle",
                              "Find your tutor",
                            ),
                            desc: t(
                              "finances:creditsGuide.student.findTutorDesc",
                              "Tutors using this system have a Direct Payment badge.",
                            ),
                          },
                          {
                            icon: "mdi:calendar-plus",
                            title: t("finances:creditsGuide.student.bookTitle", "Book your lesson"),
                            desc: t(
                              "finances:creditsGuide.student.bookDesc",
                              "The platform fee is covered by the tutor’s credits.",
                            ),
                          },
                          {
                            icon: "mdi:bank-transfer",
                            title: t("finances:creditsGuide.student.payTitle", "Pay your tutor"),
                            desc: t(
                              "finances:creditsGuide.student.payDesc",
                              "Send the lesson fee directly to the tutor using their provided methods.",
                            ),
                          },
                          {
                            icon: "mdi:check-decagram",
                            title: t(
                              "finances:creditsGuide.student.readyTitle",
                              "Get ready to learn",
                            ),
                            desc: t(
                              "finances:creditsGuide.student.readyDesc",
                              "Once the tutor confirms payment, your lesson is locked in.",
                            ),
                          },
                        ].map((row) => (
                          <div key={row.title as string} className="rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-accent-foreground"
                                height="1rem"
                                icon={row.icon}
                                width="1rem"
                              />
                              <span className="text-accent-foreground text-sm font-semibold">
                                {row.title}
                              </span>
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                              {row.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  <Separator className="bg-border/30" />

                  {/* Trust & Safety */}
                  <div>
                    <div className="flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground"
                        height="1rem"
                        icon="mdi:shield-alert"
                        width="1rem"
                      />
                      <div className="text-accent-foreground text-sm font-semibold">
                        {t(
                          "finances:creditsGuide.safety.title",
                          "Trust, safety, and your responsibility",
                        )}
                      </div>
                    </div>
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-foreground/60 hover:bg-foreground/70 relative mt-3 overflow-hidden rounded-2xl p-5 transition-all"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <motion.div
                        animate={{ opacity: [0.45, 0.6, 0.45], scale: [1.05, 1.12, 1.05] }}
                        className="absolute top-8 right-10 size-56 rounded-full bg-orange-500/35 opacity-60 blur-3xl"
                        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <motion.div
                        animate={{ opacity: [0.3, 0.5, 0.3], scale: [1, 1.08, 1] }}
                        className="absolute bottom-8 left-8 size-48 rounded-full bg-blue-500/30 opacity-50 blur-3xl"
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <div className="relative z-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[
                          {
                            icon: "mdi:alert-circle-outline",
                            title: t(
                              "finances:creditsGuide.safety.enforceTitle",
                              "Enforcing payments",
                            ),
                            desc: t(
                              "finances:creditsGuide.safety.enforceDesc",
                              "We cannot automate or enforce direct payments. Tutors should only mark a lesson as Paid after funds are received.",
                            ),
                          },
                          {
                            icon: "mdi:cash-refund",
                            title: t("finances:creditsGuide.safety.refundsTitle", "Refunds"),
                            desc: t(
                              "finances:creditsGuide.safety.refundsDesc",
                              "We do not process lesson payments, so we cannot issue refunds. Arrange refunds directly per the tutor’s policy.",
                            ),
                          },
                          {
                            icon: "mdi:account-alert",
                            title: t(
                              "finances:creditsGuide.safety.reportTitle",
                              "Reporting issues",
                            ),
                            desc: t(
                              "finances:creditsGuide.safety.reportDesc",
                              "Report rule-breaking. We’ll investigate and take appropriate action.",
                            ),
                          },
                        ].map((row) => (
                          <div key={row.title as string} className="rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-accent-foreground"
                                height="1rem"
                                icon={row.icon}
                                width="1rem"
                              />
                              <span className="text-accent-foreground text-sm font-semibold">
                                {row.title}
                              </span>
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                              {row.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </div>
              ) : selectedPaymentPlan === "stripe" ? (
                <div className="space-y-6">
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-muted/30 flex h-16 w-16 items-center justify-center rounded-xl">
                      <Icon
                        className="text-muted-foreground"
                        height="2rem"
                        icon="logos:stripe"
                        width="2rem"
                      />
                    </div>
                    <div>
                      <h3 className="text-muted-foreground mb-1 text-xl font-bold sm:text-2xl">
                        {t("finances:paymentsSetup.stripe.title", "Stripe")}
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        {t("finances:paymentsSetup.stripe.subtitle", "Dynamic Billing and Payouts")}
                      </p>
                    </div>
                  </motion.div>
                  <motion.p
                    animate={{ opacity: 1 }}
                    className="text-muted-foreground text-sm leading-relaxed sm:text-base"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    {t(
                      "finances:paymentsSetup.stripe.description",
                      "Stripe provides a comprehensive payment processing solution with dynamic billing and automated payouts. However, it may not be available in all countries.",
                    )}
                  </motion.p>
                </div>
              ) : (
                <div className="space-y-6">
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-accent-foreground/20 flex h-16 w-16 items-center justify-center rounded-xl">
                      <Icon
                        className="text-accent-foreground"
                        height="2rem"
                        icon="mdi:credit-card-outline"
                        width="2rem"
                      />
                    </div>
                    <div>
                      <h3 className="text-accent-foreground mb-1 text-xl font-bold sm:text-2xl">
                        {t("finances:paymentsSetup.defaultTitle", "Payment Plans")}
                      </h3>
                      <p className="text-muted-foreground text-sm sm:text-base">
                        {t(
                          "finances:paymentsSetup.defaultSubtitle",
                          "Choose how you want to get paid",
                        )}
                      </p>
                    </div>
                  </motion.div>
                  <motion.p
                    animate={{ opacity: 1 }}
                    className="text-muted-foreground text-sm leading-relaxed sm:text-base"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    {t(
                      "finances:paymentsSetup.defaultDescription",
                      "Setting up your payment plan is essential to start receiving payments from students. Each plan offers different advantages depending on your location and preferences. Select a plan above to view detailed information and features.",
                    )}
                  </motion.p>
                </div>
              )}
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Bottom CTA Section */}
        {selectedPaymentPlan && (
          <motion.div
            layout
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 mb-6 flex flex-col items-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{
              duration: 0.6,
              delay: 0.7,
              ease: [0.23, 1, 0.32, 1],
              layout: { duration: 0.5 },
            }}
          >
            {/* Acknowledgement */}
            <div className="w-full max-w-2xl">
              <div
                className="bg-foreground/60 hover:bg-foreground/70 flex items-center gap-3 rounded-xl px-4 py-3 transition-colors select-none"
                role="group"
              >
                <Checkbox
                  checked={isAcknowledged}
                  className="bg-accent-foreground data-[state=checked]:bg-accent-foreground! data-[state=checked]:text-primary!"
                  id="credits-ack"
                  onCheckedChange={(v) => setIsAcknowledged(v === true)}
                />
                <Label
                  className="cursor-default text-sm"
                  htmlFor="credits-ack"
                  id="credits-ack-label"
                >
                  <span className="text-accent-foreground font-medium">
                    {t(
                      "finances:creditsGuide.acknowledge.title",
                      "I confirm I have reviewed how the Direct Payment & Credits system works",
                    )}
                  </span>
                </Label>
              </div>
            </div>

            {/* Confirm Button */}
            <motion.div
              layout
              animate={{
                opacity: selectedPaymentPlan ? 1 : 0.6,
                scale: selectedPaymentPlan ? 1 : 0.98,
              }}
              className="w-full max-w-md"
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <Button
                className="min-h-[44px] w-full transition-all duration-400"
                disabled={selectedPaymentPlan !== "credit" || !isAcknowledged || isLoading}
                size="lg"
                variant="foreground"
                onClick={handleConfirmPlan}
              >
                <motion.div
                  layout
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-center gap-2"
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <span>{t("common:loading", "Setting up...")}</span>
                    </>
                  ) : selectedPaymentPlan === "credit" ? (
                    t("finances:paymentsSetup.confirmPlan", "Confirm Plan & Continue")
                  ) : (
                    t("finances:paymentsSetup.selectPlan", "Select a Payment Plan")
                  )}
                </motion.div>
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PaymentsStep;
